import { Download, History, LogOut, Zap, Languages, Crown, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const platforms = ["Facebook", "YouTube", "Instagram", "TikTok", "X"];

export const Navbar = ({ onLogin, onRegister, onOpenHistory, historyCount, onUpgrade }) => {
  const { user, logout } = useAuth();
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#090A0F]/80 border-b border-white/10">
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-3.5 flex items-center justify-between">
        <div className="flex items-center gap-2.5" data-testid="navbar-brand-logo">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-violet-600 to-cyan-500 grid place-items-center vg-glow">
            <Download size={18} className="text-white" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="text-lg font-head font-bold tracking-tight">VidGrab</span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t("brand.sub")}</span>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-1.5">
          {platforms.map((p) => (
            <span key={p} className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-white/5 border border-white/5 text-slate-400">
              {p}
            </span>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            className="flex items-center gap-1.5 px-2.5 h-9 rounded-xl bg-white/5 border border-white/10 text-xs font-semibold text-slate-200 hover:bg-white/10 transition-colors"
            data-testid="lang-toggle-button"
            title="Switch language"
          >
            <Languages size={15} />
            <span>{lang === "en" ? "हिं" : "EN"}</span>
          </button>
          {user && typeof user === "object" ? (
            <>
              {user.is_premium ? (
                <span
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 h-9 rounded-xl bg-gradient-to-r from-amber-400/20 to-orange-500/20 border border-amber-500/30 text-sm text-amber-300 font-medium"
                  data-testid="premium-badge"
                >
                  <Crown size={15} /> {t("nav.premium")}
                </span>
              ) : (
                <button
                  onClick={onUpgrade}
                  className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-sm font-semibold text-black hover:opacity-95 transition-opacity"
                  data-testid="nav-upgrade-button"
                >
                  <Crown size={15} /> {t("nav.premium")}
                </button>
              )}
              <button
                onClick={onOpenHistory}
                className="relative flex items-center gap-1.5 px-3 h-9 rounded-xl bg-white/5 border border-white/10 text-sm text-slate-200 hover:bg-white/10 transition-colors"
                data-testid="nav-history-button"
              >
                <History size={16} />
                <span className="hidden sm:inline">{t("nav.history")}</span>
                {historyCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 h-5 min-w-5 px-1 grid place-items-center text-[10px] font-bold rounded-full bg-violet-600 text-white">
                    {historyCount}
                  </span>
                )}
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="h-9 w-9 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 grid place-items-center text-sm font-bold text-white uppercase"
                    data-testid="nav-user-menu"
                  >
                    {(user.name || user.email || "U").charAt(0)}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#12141F] border-white/10 text-slate-200">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium truncate">{user.name || "User"}</p>
                    <p className="text-xs text-slate-500 truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-white/10" />
                  {user.role === "admin" && (
                    <DropdownMenuItem
                      onClick={() => navigate("/admin")}
                      className="text-amber-400 focus:text-amber-300 focus:bg-white/5 cursor-pointer"
                      data-testid="nav-admin-link"
                    >
                      <Shield size={14} className="mr-2" /> {t("nav.admin")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={logout}
                    className="text-rose-400 focus:text-rose-300 focus:bg-white/5 cursor-pointer"
                    data-testid="nav-logout-button"
                  >
                    <LogOut size={14} className="mr-2" /> {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <button
                onClick={onLogin}
                className="px-4 h-9 rounded-xl text-sm text-slate-200 hover:bg-white/5 transition-colors"
                data-testid="nav-login-button"
              >
                {t("nav.login")}
              </button>
              <button
                onClick={onRegister}
                className="flex items-center gap-1.5 px-4 h-9 rounded-xl text-sm font-semibold bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:opacity-95 transition-opacity"
                data-testid="nav-register-button"
              >
                <Zap size={15} /> {t("nav.signup")}
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};
